/**
 * PersonaAvatar — 2026-08-19.
 *
 * Replaces the real-3D WebGL snapshot pipeline (personaSnapshots.ts,
 * deleted this round) after "everyone has the same model" feedback — that
 * pipeline reused one CC0 rig recolored/rotated per variant, which reads as
 * one person, not distinct personas. This is original flat-cartoon avatar
 * art instead: hand-authored SVG, one look per variant, each wearing a
 * headset + mic boom (the actual ask — "cartoonish models with mic
 * headphones"). No external assets, so no licensing question at all, and
 * no async generation/capture step (no loading state, no blank-capture
 * failure mode the WebGL approach had).
 *
 * Purely geometric (circles/paths), not a specific reference — six looks
 * varied by skin tone, hairstyle, and headset accent color so they read as
 * different people at a glance, even at the 44px grid-card size.
 */
import React from 'react';

interface PersonaLook {
  skin: string;
  hair: string;
  hairStyle: 'short' | 'bun' | 'curly' | 'side' | 'quiff' | 'bald';
  shirt: string;
}

const PERSONA_LOOKS: PersonaLook[] = [
  { skin: '#E8B08A', hair: '#3B2A21', hairStyle: 'short', shirt: '#4A5568' },
  { skin: '#8D5A3C', hair: '#161616', hairStyle: 'bun', shirt: '#3F4759' },
  { skin: '#F2C9A0', hair: '#8A5A2B', hairStyle: 'curly', shirt: '#54494F' },
  { skin: '#C98455', hair: '#241A14', hairStyle: 'side', shirt: '#465146' },
  { skin: '#F6D9B8', hair: '#5B4636', hairStyle: 'quiff', shirt: '#4B4458' },
  { skin: '#B97A52', hair: '#0F0F0F', hairStyle: 'bald', shirt: '#455066' },
];

export const PERSONA_LOOK_COUNT = PERSONA_LOOKS.length;

function Hair({ style, color }: { style: PersonaLook['hairStyle']; color: string }) {
  switch (style) {
    case 'short':
      return <path d="M28 40 Q30 16 50 15 Q70 16 72 40 Q72 26 50 24 Q28 26 28 40 Z" fill={color} />;
    case 'bun':
      return (
        <>
          <path d="M27 38 Q29 17 50 16 Q71 17 73 38 Q73 24 50 22 Q27 24 27 38 Z" fill={color} />
          <circle cx="50" cy="12" r="7" fill={color} />
        </>
      );
    case 'curly':
      return (
        <>
          {[[32, 24], [42, 16], [50, 13], [58, 16], [68, 24], [36, 34], [64, 34]].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r={7.5} fill={color} />
          ))}
        </>
      );
    case 'side':
      return <path d="M27 40 Q26 15 52 14 Q73 16 71 34 Q66 20 46 18 Q30 19 29 40 Z" fill={color} />;
    case 'quiff':
      return (
        <>
          <path d="M29 38 Q30 22 50 21 Q70 22 71 38 Q71 28 50 27 Q29 28 29 38 Z" fill={color} />
          <path d="M42 22 Q50 6 60 20 Q52 14 46 22 Z" fill={color} />
        </>
      );
    case 'bald':
    default:
      return <path d="M30 33 Q31 24 50 24 Q69 24 70 33" stroke={color} strokeWidth="0" fill="none" />;
  }
}

/** One flat-cartoon customer-service avatar: head, hair, headset with mic
 * boom, and shoulders. `variant` selects the look, `accent` tints the
 * headset/mic to match the persona's badge color. */
export const PersonaAvatar: React.FC<{ variant: number; accent: string; className?: string }> = ({
  variant,
  accent,
  className,
}) => {
  const look = PERSONA_LOOKS[((variant % PERSONA_LOOKS.length) + PERSONA_LOOKS.length) % PERSONA_LOOKS.length];
  return (
    <svg viewBox="0 0 100 100" className={className} width="100%" height="100%" aria-hidden="true">
      {/* shoulders */}
      <path d="M12 100 Q18 74 50 74 Q82 74 88 100 Z" fill={look.shirt} />
      {/* neck */}
      <rect x="43" y="58" width="14" height="16" rx="4" fill={look.skin} />
      {/* head */}
      <circle cx="50" cy="42" r="24" fill={look.skin} />
      {/* hair */}
      <Hair style={look.hairStyle} color={look.hair} />
      {/* ears */}
      <circle cx="27" cy="43" r="4" fill={look.skin} />
      <circle cx="73" cy="43" r="4" fill={look.skin} />
      {/* face */}
      <circle cx="41" cy="43" r="2.6" fill="#2b2320" />
      <circle cx="59" cy="43" r="2.6" fill="#2b2320" />
      <path d="M41 53 Q50 59 59 53" stroke="#7a4a34" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <ellipse cx="34" cy="49" rx="3.4" ry="2.2" fill="#ff9d85" opacity="0.35" />
      <ellipse cx="66" cy="49" rx="3.4" ry="2.2" fill="#ff9d85" opacity="0.35" />
      {/* headset band */}
      <path d="M25 38 Q28 12 50 12 Q72 12 75 38" stroke={accent} strokeWidth="4.5" fill="none" strokeLinecap="round" />
      {/* ear cups */}
      <circle cx="25" cy="42" r="7" fill={accent} />
      <circle cx="75" cy="42" r="7" fill={accent} />
      <circle cx="25" cy="42" r="3.2" fill="#ffffff" opacity="0.55" />
      <circle cx="75" cy="42" r="3.2" fill="#ffffff" opacity="0.55" />
      {/* mic boom */}
      <path d="M75 47 Q68 58 54 58" stroke={accent} strokeWidth="2.6" fill="none" strokeLinecap="round" />
      <circle cx="53" cy="58" r="3" fill={accent} />
    </svg>
  );
};

export default PersonaAvatar;
